import { useCallback } from "react";

import { Check } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { ActionableInsight } from "app/pages/activation/types/api";

import { buildInsightCopyPlainText } from "../helpers/buildInsightCopyPlainText";
import { useCopySuccessFeedback } from "../helpers/useCopySuccessFeedback";
import * as SC from "../styles";
import { INSIGHTS_FEEDBACK_COPIED, INSIGHTS_FEEDBACK_COPY, INSIGHTS_FEEDBACK_COPY_FAILED } from "../texts";

type InsightFeedbackCopyButtonProps = {
    insight: ActionableInsight;
    dataAutomation: string;
};

export const InsightFeedbackCopyButton = ({ insight, dataAutomation }: InsightFeedbackCopyButtonProps) => {
    const { isCopied, notifyCopySuccess } = useCopySuccessFeedback();
    const { showErrorToast } = useToasts();
    const tracking = activationTracking.homePageInsights;

    const handleClick = useCallback(async () => {
        try {
            const text = buildInsightCopyPlainText(insight);

            await navigator.clipboard.writeText(text);
            notifyCopySuccess();
            tracking.trackInsightCopy(text);
        } catch (error) {
            Sentry.errorTracker({
                e: error instanceof Error ? error : new Error(String(error)),
                message: "Failed to copy insight to clipboard",
                level: "warning",
                params: { extra: { insightTitle: insight.title } },
            });

            showErrorToast(translate(INSIGHTS_FEEDBACK_COPY_FAILED));
        }
    }, [insight, notifyCopySuccess, showErrorToast, tracking]);

    return (
        <Tooltip
            content={translate(isCopied ? INSIGHTS_FEEDBACK_COPIED : INSIGHTS_FEEDBACK_COPY)}
            placement="bottom"
        >
            {isCopied ? (
                <SC.CheckedIconContainer>
                    <Check
                        size="sm"
                        color={colorsPalettes.carbon[200]}
                    />
                </SC.CheckedIconContainer>
            ) : (
                <SC.StyledIconButton
                    type="flat"
                    iconName={"copy-rounded"}
                    iconSize="xs"
                    dataAutomation={dataAutomation}
                    onClick={() => void handleClick()}
                />
            )}
        </Tooltip>
    );
};
